import React, { useRef } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import VariablePicker from './VariablePicker';

/**
 * A drop-in replacement for MUI TextField that appends a VariablePicker button.
 * Clicking a variable from the picker inserts it at the current cursor position.
 *
 * Accepts all standard TextField props plus:
 *   screens         – all flow screens (passed down from context)
 *   currentScreenId – ID of the screen being edited
 *
 * If screens is not provided, the variable picker is omitted.
 */
const VariableTextField = ({ screens, currentScreenId, onChange, InputProps, ...rest }) => {
  const inputRef = useRef(null);

  const handleInsert = (variable) => {
    const el = inputRef.current?.querySelector('input,textarea');
    if (!el) return;

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const current = el.value;
    const next = current.slice(0, start) + variable + current.slice(end);

    // Fire synthetic change event compatible with React state
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
      'value'
    )?.set;
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(el, next);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Move cursor after the inserted variable
    const newPos = start + variable.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newPos, newPos);
    });
  };

  const endAdornment = screens ? (
    <InputAdornment position="end">
      <VariablePicker
        screens={screens}
        currentScreenId={currentScreenId}
        onInsert={handleInsert}
      />
    </InputAdornment>
  ) : null;

  return (
    <TextField
      {...rest}
      onChange={onChange}
      inputRef={inputRef}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            {endAdornment}
          </>
        ),
      }}
    />
  );
};

export default VariableTextField;
